/**
 * Journal helpers shared by lessons, studio, challenges and manual uploads.
 */
import { enqueueSync } from './syncQueue';
import { applyFilter, composeLetter, LETTERS, type Drawing, type ItemSticker, type JournalFilter, type Letter } from '@sketchling/core';
import { appNow, uid, useApp, type JournalItem, type JournalSource } from '../state/store';
import { putImage } from './idb';
import { api, drawingMeta, uploadPendingDrawings } from './api';
import { play } from './sfx';

export interface SaveDrawingInput {
  profileId: string;
  blob: Blob | null;
  source: JournalSource;
  drawing?: Drawing;
  title?: string;
  subject?: string;
  partnerProfileId?: string;
  feedback?: JournalItem['feedback'];
}

/** Store the photo in IndexedDB and add a journal item (offline-first; uploaded on next sync). */
export async function saveToJournal(input: SaveDrawingInput, titleFallback: string): Promise<JournalItem> {
  const id = uid();
  let imageKey = '';
  if (input.blob) {
    imageKey = `img-${id}`;
    await putImage(imageKey, input.blob);
  }
  const item: JournalItem = {
    id,
    profileId: input.profileId,
    imageKey,
    createdAt: appNow().toISOString(),
    title: input.title ?? titleFallback,
    description: '',
    feeling: null,
    filter: 'none',
    drawingId: input.drawing?.id,
    subject: input.subject ?? input.drawing?.subject ?? 'drawing',
    category: input.drawing?.category,
    skill: input.drawing?.skills[0],
    source: input.source,
    comments: [],
    ...(input.partnerProfileId ? { partnerProfileId: input.partnerProfileId } : {}),
    ...(input.feedback ? { feedback: input.feedback } : {}),
  };
  useApp.getState().addJournalItem(item);
  useApp.getState().logEvent('Journal Item Added', { source: input.source, subject: item.subject });
  play('cardAdded');
  void uploadPendingDrawings();
  return item;
}

/* ------------------------------- Canvas helpers ------------------------------- */

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function canvasToBlob(c: HTMLCanvasElement, type = 'image/jpeg', quality = 0.85): Promise<Blob> {
  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), type, quality));
}

/** Re-encode a photo with a journal filter applied (pure pixel filter from core). */
export async function filteredBlob(blob: Blob, filter: JournalFilter, maxSide = 1200): Promise<Blob> {
  if (filter === 'none') return blob;
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const s = Math.min(1, maxSide / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * s);
    c.height = Math.round(img.height * s);
    const ctx = c.getContext('2d')!;
    ctx.drawImage(img, 0, 0, c.width, c.height);
    const data = ctx.getImageData(0, 0, c.width, c.height);
    const out = applyFilter({ width: data.width, height: data.height, data: data.data }, filter);
    ctx.putImageData(new ImageData(out.data, out.width, out.height), 0, 0);
    return await canvasToBlob(c);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** CSS approximation of each filter for live previews (the saved file uses the pixel filter). */
export const FILTER_CSS: Record<JournalFilter, string> = {
  none: 'none',
  clean: 'contrast(1.25) brightness(1.08)',
  warm: 'sepia(0.25) saturate(1.2) brightness(1.04)',
  cool: 'hue-rotate(-12deg) saturate(1.1) brightness(1.03)',
  mono: 'grayscale(1) contrast(1.1)',
  pop: 'saturate(1.6) contrast(1.2)',
};

/**
 * Teacher notes on journal drawings ("external feedback"): when a letter is delivered, the teacher's
 * per-drawing note is also left as a comment on each sent drawing (unread → journal badge + "new" chip).
 * Idempotent: one comment per letter and drawing.
 */
export function addTeacherNotes(profileId: string, letter: Letter): void {
  const st = useApp.getState();
  const profile = st.profiles.find((p) => p.id === profileId);
  if (!profile) return;
  const composed = composeLetter(letter, LETTERS, { name: profile.name, age: profile.age, lang: st.settings.language });
  for (const note of composed.notes) {
    const item = useApp.getState().journal.find((j) => j.id === note.journalId);
    const id = `teacher-${letter.id}-${note.journalId}`;
    if (!item || item.comments.some((c) => c.id === id)) continue;
    useApp.getState().updateJournalItem(item.id, {
      comments: [...item.comments, { id, commenter: 'teacher', text: note.text, createdAt: letter.deliveredAt, seen: false }],
    });
  }
}

/* ------------------------------ Stickers by item ------------------------------ */


/**
 * Replace the stickers on one journal drawing. When the drawing has a cloud copy the new layout is
 * pushed with its metadata (debounced), so stickers follow the drawing to other devices.
 */
export function setItemStickers(itemId: string, stickers: ItemSticker[]): void {
  useApp.getState().updateJournalItem(itemId, { stickers });
  // Optimistic: shown at once; the background queue patches the cloud copy (deduped per drawing, retried offline).
  enqueueSync('drawingMeta', `drawingMeta:${itemId}`, null, 700);
}
